import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { it, enUS } from 'date-fns/locale';
import { MessageCircle } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import CommentItem from '../comments/CommentItem';
import MentionTextarea from '../comments/MentionTextarea';
import { sendsService } from '../../services/sends';
import { commentsService } from '../../services/comments';
import { useAuth } from '../../hooks/useAuth';
import { formatSendDate } from './send-date';
import { SendAttemptsLabel } from './SendAttemptsLabel';
import type { Send } from '../../types/send';
import type { Comment } from '../../types/comment';

interface ClimbSendsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  climbSlug: string;
  climbName: string;
  /** Fired after a comment is posted or removed, so a count elsewhere can follow. */
  onCommentsChanged?: () => void;
}

/**
 * Who has sent a climb, when, and in how many goes — with each send's comments
 * under it and a box to add one.
 *
 * Two calls rather than one per send: the sends endpoint knows who and when,
 * the climb's grouped comments endpoint knows what was said. Comments are shown
 * straight away rather than behind a tap, because on a crag's topo the
 * conversation under a line (the beta, the state of the bolts) is half of what
 * the list is read for. Tapping a send opens the box to answer it.
 */
export default function ClimbSendsDrawer({
  open,
  onOpenChange,
  climbSlug,
  climbName,
  onCommentsChanged,
}: ClimbSendsDrawerProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const dateLocale = i18n.language === 'it' ? it : enUS;

  const [sends, setSends] = useState<Send[]>([]);
  const [commentsByLog, setCommentsByLog] = useState<Record<string, Comment[]>>({});
  const [loading, setLoading] = useState(false);
  // Which send has its composer open. Only one at a time: the drawer is a
  // phone-height sheet, and every open box pushes the rest of the list away.
  const [composingFor, setComposingFor] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [sendsRes, commentsRes] = await Promise.all([
          sendsService.getClimbSends(climbSlug),
          commentsService.getCommentsForClimb(climbSlug).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;

        setSends(
          [...sendsRes.data].sort(
            (a, b) => new Date(b.attributes.sent_at).getTime() - new Date(a.attributes.sent_at).getTime()
          )
        );
        setCommentsByLog(
          Object.fromEntries(
            commentsRes.data.map((section) => [section.ascent_log.id, section.comments])
          )
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, climbSlug]);

  // A fresh open starts with nothing half-written from last time.
  useEffect(() => {
    if (open) return;
    setComposingFor(null);
    setDraft('');
  }, [open]);

  const handlePost = useCallback(
    async (ascentLogId: string) => {
      const body = draft.trim();
      if (!body) return;

      setSubmitting(true);
      try {
        const res = await commentsService.createComment(ascentLogId, body);
        setCommentsByLog((prev) => ({
          ...prev,
          [ascentLogId]: [...(prev[ascentLogId] ?? []), res.data],
        }));
        setDraft('');
        onCommentsChanged?.();
      } finally {
        setSubmitting(false);
      }
    },
    [draft, onCommentsChanged]
  );

  // Answering someone drops their handle into the box, in the mention syntax
  // MentionTextarea round-trips.
  const handleReply = useCallback((ascentLogId: string, author: Comment['user']) => {
    const mention = `@[${author.username}](${author.slug}) `;
    setComposingFor(ascentLogId);
    setDraft((current) => {
      if (current.includes(mention)) return current;
      const separator = current && !current.endsWith(' ') ? ' ' : '';
      return current + separator + mention;
    });
  }, []);

  const replaceComment = (ascentLogId: string, updated: Comment) =>
    setCommentsByLog((prev) => ({
      ...prev,
      [ascentLogId]: (prev[ascentLogId] ?? []).map((c) => (c.id === updated.id ? updated : c)),
    }));

  const removeComment = (ascentLogId: string, commentId: string) => {
    setCommentsByLog((prev) => ({
      ...prev,
      [ascentLogId]: (prev[ascentLogId] ?? []).filter((c) => c.id !== commentId),
    }));
    onCommentsChanged?.();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {climbName} — {t('sends.title')} {sends.length > 0 && `(${sends.length})`}
          </SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : sends.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t('sends.noSends')}</div>
        ) : (
          <div className="mt-4 space-y-4" data-testid="climb-sends-drawer-list">
            {sends.map((send) => {
              const ascentLogId = send.attributes.ascent_log_id;
              const sender = send.attributes.user;
              const comments = ascentLogId ? commentsByLog[ascentLogId] ?? [] : [];
              // Without an ascent log id there is nothing to hang a comment on
              // (an old send serialized before the id was exposed).
              const canComment = !!user && !!ascentLogId;

              return (
                <div key={send.id} className="space-y-3 border-b pb-4 last:border-b-0">
                  <button
                    type="button"
                    className={`flex w-full items-center gap-3 text-left ${canComment ? 'cursor-pointer' : 'cursor-default'}`}
                    onClick={
                      canComment
                        ? () => {
                            setComposingFor((current) => (current === ascentLogId ? null : ascentLogId!));
                            setDraft('');
                          }
                        : undefined
                    }
                  >
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarImage src={sender.avatar_small_url} alt={sender.username} />
                      <AvatarFallback>{sender.username?.charAt(0)?.toUpperCase() || 'U'}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{sender.username}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatSendDate(send.attributes.sent_at, t, dateLocale)}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 text-sm">
                      {comments.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <MessageCircle className="h-3.5 w-3.5" />
                          {comments.length}
                        </span>
                      )}
                      <SendAttemptsLabel
                        sendType={send.attributes.send_type}
                        attemptsCount={send.attributes.attempts_count}
                      />
                    </div>
                  </button>

                  {comments.length > 0 && ascentLogId && (
                    <div className="space-y-3 pl-12">
                      {comments.map((comment) => (
                        <CommentItem
                          key={comment.id}
                          comment={comment}
                          currentUserId={user?.id?.toString()}
                          ascentLogId={ascentLogId}
                          onUpdated={(updated) => replaceComment(ascentLogId, updated)}
                          onDeleted={(id) => removeComment(ascentLogId, id)}
                          onReply={canComment ? (author) => handleReply(ascentLogId, author) : undefined}
                        />
                      ))}
                    </div>
                  )}

                  {canComment && composingFor === ascentLogId && (
                    <div className="space-y-2 pl-12">
                      <MentionTextarea
                        value={draft}
                        onChange={setDraft}
                        disabled={submitting}
                        minRows={2}
                      />
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          onClick={() => handlePost(ascentLogId!)}
                          disabled={submitting || !draft.trim()}
                        >
                          {t('comments.send')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
